import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, Subscription } from 'rxjs';
import { copyDetails } from 'src/app/core/dictionary';
import { PagamentoService } from 'src/app/core/services/pagamento.service';
import {
  FormaPagamento,
  Invoice,
  Locale,
  VerifyCardRequestDto,
  VerifyCardResponseDto,
} from 'src/app/core/types';
import Swal from 'sweetalert2';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';

@Component({
  selector: 'app-payment-cielo',
  templateUrl: './payment-cielo.component.html',
  styleUrls: ['./payment-cielo.component.scss'],
})
export class PaymentCieloComponent implements OnInit, OnDestroy {
  paymentForm!: FormGroup;
  cardVerify: VerifyCardResponseDto | null = null;
  cardBrandIcon: string | null = null;
  private subscription: Subscription = new Subscription();
  loadingPayment = false;
  public locale: Locale = 'ptBR';
  public copy = copyDetails;
  paymentSuccess = false;
  public parcelas?: { value: number; label: string; }[];
  public permiteParcelamento: boolean = false;

  @Input() formaPagamento: FormaPagamento = {} as FormaPagamento;
  @Input() invoice: Invoice = {} as Invoice;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private pagamentoService: PagamentoService,
    private analytics: AngularFireAnalytics
  ) {}

  ngOnInit() {
    this.paymentForm = this.formBuilder.group({
      installments: [
        {
          value: 1,
          disabled:
            this.loadingPayment || this.formaPagamento?.tipo !== 'CARTAO',
        },
        [Validators.required],
      ],
      surname: [
        { value: '', disabled: this.loadingPayment },
        [Validators.required],
      ],
      cardNumber: [
        { value: '', disabled: this.loadingPayment },
        [Validators.required],
      ],
      validity: [
        { value: '', disabled: this.loadingPayment },
        [Validators.required],
      ],
      cvc: [
        { value: '', disabled: this.loadingPayment },
        [Validators.required],
      ],
    });

    var sub = this.paymentForm
      .get('cardNumber')!
      .valueChanges.pipe(
        debounceTime(300),
        map((value: string) => value.replace(/\D/g, '').slice(0, 6)),
        distinctUntilChanged()
      )
      .subscribe((bin) => {
        if (bin.length === 6) {
          this.lookupBin(bin);
        } else {
          this.cardBrandIcon = null;
        }
      });

    this.subscription.add(sub);
    
    this.pagamentoService.getParcelamento(this.invoice.uuid).subscribe({
      next: (parcelamento) => {
        this.permiteParcelamento = parcelamento.permiteParcelamento;

        if (parcelamento.permiteParcelamento) {
          this.parcelas = Array.from({ length: parcelamento.numeroParcelas ?? 1 }, (_, i) => ({
            value: i + 1,
            label: i === 0 ? '1x à vista' : `${i + 1}x sem juros`,
          }));

          this.paymentForm.get('installments')?.enable();
        } else {
          this.paymentForm.get('installments')?.disable();
        }
      }
    });
  }

  lookupBin(bin: string) {
    var payload: VerifyCardRequestDto = {
      Card: {
        CardNumber: bin,
      },
    };

    this.analytics.logEvent('bin_lookup_attempt', {
      invoice_uuid: this.invoice.uuid,
      bin: bin,
    });

    this.pagamentoService.searchBin(payload).subscribe({
      next: (cardVerify) => {
        this.cardVerify = cardVerify;
        var brand = cardVerify?.BinData.Provider?.toLowerCase();
        this.cardBrandIcon = this.getBrandIconUrl(brand);

        this.analytics.logEvent('bin_lookup_success', {
          invoice_uuid: this.invoice.uuid,
          bin: bin,
          brand: cardVerify?.BinData?.Provider,
          card_type: cardVerify?.BinData?.CardType,
        });
      },
      error: (error) => {
        this.cardBrandIcon = null;

        this.analytics.logEvent('bin_lookup_error', {
          invoice_uuid: this.invoice.uuid,
          bin: bin,
          status: error.status,
          message: error.error?.message,
          response: JSON.stringify(error.error || {}),
        });

        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: error.error?.message || 'Erro ao consultar o cartão',
          confirmButtonColor: '#92C5CE',
        });
      },
    });
  }

  get isCredito(): boolean {
    return this.formaPagamento?.tipo === 'CARTAO';
  }

  get isDebito(): boolean {
    return this.formaPagamento?.tipo === 'CARTAO_DEBITO';
  }

  getBrandIconUrl(brand: string): string | null {
    var brandIcons: Record<string, string> = {
      visa: '/assets/images/visa.svg',
      master: '/assets/images/mastercard.svg',
      amex: '/assets/images/amex.svg',
      elo: '/assets/images/elo.svg',
      hipercard: '/assets/images/hipercard.svg',
      hiper: '/assets/images/hiper.svg',
      discover: '/assets/images/discover.svg',
      diners: '/assets/images/diners.svg',
    };
    return brandIcons[brand] || null;
  }

  payWithCard() {
    if (this.loadingPayment || this.paymentForm.invalid) return;
    this.loadingPayment = true;
    this.paymentForm.disable();

    var apelido = this.paymentForm.get('surname')?.value;
    var vencimento = this.paymentForm.get('validity')?.value;
    var mesVencimento = Number(vencimento.slice(0, 2));
    var anoVencimento = 2000 + Number(vencimento.slice(2, 4));
    var cardNumber = this.paymentForm.get('cardNumber')?.value;
    var cvc = this.paymentForm.get('cvc')?.value;
    var numeroParcelas =
      this.formaPagamento?.tipo === 'CARTAO'
        ? Number(this.paymentForm.get('installments')?.value)
        : 1;

    var cartao: { [key: string]: any } = {
      bandeira: this.cardVerify?.BinData?.Provider,
      tipoCartao: this.cardVerify?.BinData?.CardType,
      numeroParcelas: numeroParcelas,
      digitos: cardNumber,
      mesVencimento: mesVencimento,
      anoVencimento: anoVencimento,
      cvc: cvc,
      apelido: apelido,
    };

    var dto = {
      uuidFormaPagamento: this.formaPagamento.uuid,
      dadosPagamento: cartao,
    };

    this.analytics.logEvent('payment_attempt', {
      invoice_uuid: this.invoice.uuid,
      provider: this.cardVerify?.BinData?.Provider,
      card_type: this.cardVerify?.BinData?.CardType,
      installments: numeroParcelas,
      alias: apelido,
      timestamp: new Date().toISOString(),
    });

    this.pagamentoService.payInvoice(this.invoice.uuid, dto).subscribe({
      next: (cobranca) => {
        this.loadingPayment = false;
        this.paymentSuccess = true;
        this.paymentForm.enable();

        this.analytics.logEvent('payment_result', {
          invoice_uuid: this.invoice.uuid,
          status: cobranca.status,
          timestamp: new Date().toISOString(),
        });

        setTimeout(() => {
          window.location.reload();
        }, 5000);

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            providerRedirect:
              cobranca.status == 'PAGA' ? 'succeeded' : 'failure',
          },
          queryParamsHandling: 'merge',
        });
      },

      error: (error) => {
        this.loadingPayment = false;
        this.paymentForm.enable();

        this.analytics.logEvent('payment_error', {
          invoice_uuid: this.invoice.uuid,
          status_code: error.status,
          api_message: error.error?.message,
          full_error: JSON.stringify(error),
          timestamp: new Date().toISOString(),
        });

        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: error.error?.message || 'Houve um problema com o pagamento!\nEntre em contato com o suporte da JFL para mais informações.',
          confirmButtonColor: '#92C5CE',
        });
      },
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
